/**
 * App store — Zustand. Auth identity + UI flags (theme, sidebar).
 * Domain data is NOT duplicated here (TanStack Query handles that).
 */
import { create } from 'zustand';

export interface CurrentUser {
  id: string;
  email: string;
  name: string;
  pictureUrl: string | null;
  role: 'Admin' | 'Manager' | 'Employee';
  employeeId: string | null;
}

type Theme = 'light' | 'dark';

function getInitialTheme(): Theme {
  if (typeof window === 'undefined') return 'light';
  const stored = localStorage.getItem('vibe-theme');
  if (stored === 'light' || stored === 'dark') return stored;
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function applyTheme(theme: Theme) {
  if (typeof document !== 'undefined') {
    document.documentElement.classList.toggle('dark', theme === 'dark');
  }
}

interface AppState {
  user: CurrentUser | null;
  actualUser: CurrentUser | null;
  viewAsRole: Exclude<CurrentUser['role'], 'Admin'> | null;
  setUser: (user: CurrentUser | null) => void;
  setViewAsRole: (role: Exclude<CurrentUser['role'], 'Admin'> | null) => void;
  sidebarOpen: boolean;
  toggleSidebar: () => void;
  setSidebar: (open: boolean) => void;
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
}

const initialTheme = getInitialTheme();
applyTheme(initialTheme);

export const useAuthStore = create<AppState>((set, get) => ({
  user: null,
  actualUser: null,
  viewAsRole: null,
  setUser: (user) => set((state) => {
    if (!user) {
      return { user: null, actualUser: null, viewAsRole: null };
    }

    const viewAsRole = user.role === 'Admin' ? state.viewAsRole : null;
    return {
      actualUser: user,
      viewAsRole,
      user: viewAsRole ? { ...user, role: viewAsRole } : user,
    };
  }),
  setViewAsRole: (role) => set((state) => {
    if (state.actualUser?.role !== 'Admin') return {};
    return {
      viewAsRole: role,
      user: role ? { ...state.actualUser, role } : state.actualUser,
    };
  }),
  sidebarOpen: false,
  toggleSidebar: () => set((s) => ({ sidebarOpen: !s.sidebarOpen })),
  setSidebar: (open) => set({ sidebarOpen: open }),
  theme: initialTheme,
  toggleTheme: () => {
    const next = get().theme === 'dark' ? 'light' : 'dark';
    localStorage.setItem('vibe-theme', next);
    applyTheme(next);
    set({ theme: next });
  },
  setTheme: (theme) => {
    localStorage.setItem('vibe-theme', theme);
    applyTheme(theme);
    set({ theme });
  },
}));
